import { useState, type FormEvent, type ReactNode } from "react";
import { apiFetch, ApiError } from "../lib/apiFetch";
import type { ContactCreate, ContactSource } from "../types/api";
import { Input } from "./Input";
import { Select } from "./Select";
import { Textarea } from "./Textarea";
import { Button } from "./Button";

/** One selectable topic. `value` is sent to the backend (staff-facing, keep it
 *  canonical English); `label` is what the user sees (may be localized). */
export interface ContactTopic {
  value: string;
  label: string;
}

/** Prefill for the email/phone fields. A `*ReadOnly` flag locks the matching
 *  field to its prefilled value, and the locked value is submitted. */
export interface ContactMessagePrefill {
  email?: string | undefined;
  phone?: string | undefined;
  emailReadOnly?: boolean | undefined;
  phoneReadOnly?: boolean | undefined;
}

/** All user-visible strings of the message tab. The customer app supplies
 *  localized copy; partner/member rely on the English defaults. The modal title
 *  and tab labels live on `ContactUsModalCopy`. */
export interface ContactMessageCopy {
  emailLabel: string;
  phoneLabel: string;
  topicLabel: string;
  topicPlaceholder: string;
  messageLabel: string;
  submitLabel: string;
  submittingLabel: string;
  cancelLabel: string;
  successBody: string;
  doneLabel: string;
  contactRequiredError: string;
  topicRequiredError: string;
  messageRequiredError: string;
  submitError: string;
}

const DEFAULT_CONTACT_MESSAGE_COPY: ContactMessageCopy = {
  emailLabel: "Email",
  phoneLabel: "Phone",
  topicLabel: "Topic",
  topicPlaceholder: "Select a topic",
  messageLabel: "Message",
  submitLabel: "Send",
  submittingLabel: "Sending…",
  cancelLabel: "Cancel",
  successBody: "Thanks — we'll be in touch.",
  doneLabel: "Done",
  contactRequiredError: "Enter an email or phone number so we can reply.",
  topicRequiredError: "Select a topic.",
  messageRequiredError: "Enter a message.",
  submitError: "Please check your details and try again.",
};

export interface ContactMessageFormProps {
  onClose: () => void;
  source: ContactSource;
  topics: ContactTopic[];
  prefill?: ContactMessagePrefill | undefined;
  /** `import.meta.env.VITE_API_BASE_URL ?? ""` from the calling SPA. */
  baseUrl: string;
  /** Optional bearer (member or partner session). `/contact` is public, so
   *  omitting it is harmless. */
  token?: string | undefined;
  /** Localized overrides; anything omitted falls back to the English defaults. */
  copy?: Partial<ContactMessageCopy> | undefined;
}

interface FormErrors {
  contact?: string;
  topic?: string;
  message?: string;
}

/**
 * The "Message Us" tab of `ContactUsModal`, which posts a `ContactCreate` to the public endpoint.
 *
 * Delivery is best-effort: only a 422 surfaces as an error, and every other
 * outcome shows the confirmation. The form mounts fresh each time the modal
 * opens and on tab switch, so its fields reset from `prefill` without a reset
 * effect.
 */
export function ContactMessageForm({
  onClose,
  source,
  topics,
  prefill,
  baseUrl,
  token,
  copy: copyOverrides,
}: ContactMessageFormProps): ReactNode {
  const copy: ContactMessageCopy = { ...DEFAULT_CONTACT_MESSAGE_COPY, ...copyOverrides };
  const [email, setEmail] = useState(prefill?.email ?? "");
  const [phone, setPhone] = useState(prefill?.phone ?? "");
  const [topic, setTopic] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState(""); // honeypot; humans leave it empty
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (submitted) {
    return (
      <div>
        <p
          role="status"
          className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800"
        >
          {copy.successBody}
        </p>
        <div className="mt-6 flex justify-end">
          <Button onClick={onClose}>{copy.doneLabel}</Button>
        </div>
      </div>
    );
  }

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    const em = email.trim();
    const ph = phone.trim();
    const msg = message.trim();
    const next: FormErrors = {};
    if (!em && !ph) next.contact = copy.contactRequiredError;
    if (!topic) next.topic = copy.topicRequiredError;
    if (!msg) next.message = copy.messageRequiredError;
    setErrors(next);
    setSubmitError(null);
    if (next.contact || next.topic || next.message) return;

    setSubmitting(true);
    const payload: ContactCreate = {
      source,
      email: em || null,
      phone: ph || null,
      topic,
      message: msg,
      website,
    };
    const options = { baseUrl, ...(token ? { token } : {}) };
    apiFetch(
      "/api/contact",
      { method: "POST", body: payload as unknown as Record<string, unknown> },
      options,
    )
      .then(() => setSubmitted(true))
      .catch((err: unknown) => {
        setSubmitting(false);
        // The endpoint is best-effort (202). Only a 422 is input the user can
        // fix, so every other outcome shows success.
        if (err instanceof ApiError && err.status === 422) {
          setSubmitError(copy.submitError);
        } else {
          setSubmitted(true);
        }
      });
  };

  return (
    <form className="space-y-4" onSubmit={onSubmit} noValidate>
      <Input
        label={copy.emailLabel}
        type="email"
        autoComplete="email"
        value={email}
        disabled={prefill?.emailReadOnly}
        onChange={(e) => setEmail(e.target.value)}
      />
      <Input
        label={copy.phoneLabel}
        type="tel"
        autoComplete="tel"
        value={phone}
        disabled={prefill?.phoneReadOnly}
        onChange={(e) => setPhone(e.target.value)}
      />
      {errors.contact && (
        <p role="alert" className="text-xs text-red-700">
          {errors.contact}
        </p>
      )}
      <Select
        label={copy.topicLabel}
        value={topic}
        error={errors.topic}
        required
        onChange={(e) => setTopic(e.target.value)}
      >
        <option value="">{copy.topicPlaceholder}</option>
        {topics.map((t) => (
          <option key={t.value} value={t.value}>
            {t.label}
          </option>
        ))}
      </Select>
      <Textarea
        label={copy.messageLabel}
        value={message}
        error={errors.message}
        required
        rows={4}
        onChange={(e) => setMessage(e.target.value)}
      />

      {/* Honeypot: off-screen, non-focusable, hidden from AT. A filled value is a bot. */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        className="pointer-events-none absolute -left-[9999px] top-0 h-0 w-0 overflow-hidden opacity-0"
      />

      {submitError && (
        <p
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          {submitError}
        </p>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
          {copy.cancelLabel}
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting ? copy.submittingLabel : copy.submitLabel}
        </Button>
      </div>
    </form>
  );
}
